import { useEffect, useRef } from 'react'
import { useMultibandTrackVolume } from '@livekit/components-react'
import type { TrackReferenceOrPlaceholder } from '@livekit/components-core'

interface WaveformVisualizerProps {
  trackRef?: TrackReferenceOrPlaceholder
  className?: string
  color?: string
  lineWidth?: number
}

export function WaveformVisualizer({
  trackRef,
  className = '',
  color = '#78716c', // stone-500
  lineWidth = 2,
}: WaveformVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationRef = useRef<number | null>(null)
  const smoothedVolumesRef = useRef<number[]>([0, 0, 0, 0, 0])
  const phaseRef = useRef(0)

  // Get audio levels from LiveKit (5 frequency bands)
  const volumes = useMultibandTrackVolume(trackRef, {
    bands: 5,
    loPass: 100,
    hiPass: 600,
  })

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const draw = () => {
      // Handle high DPI displays
      const dpr = window.devicePixelRatio || 1
      const rect = canvas.getBoundingClientRect()

      if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
        canvas.width = rect.width * dpr
        canvas.height = rect.height * dpr
        ctx.scale(dpr, dpr)
      }

      // Clear canvas
      ctx.clearRect(0, 0, rect.width, rect.height)

      // Smooth the volumes - higher value = sharper/faster response
      const smoothingFactor = 0.35
      for (let i = 0; i < 5; i++) {
        const targetVolume = volumes[i] || 0
        smoothedVolumesRef.current[i] +=
          (targetVolume - smoothedVolumesRef.current[i]) * smoothingFactor
      }

      const smoothedVolumes = smoothedVolumesRef.current

      // Draw smooth sine wave based on audio levels
      const centerY = rect.height / 2
      const width = rect.width

      ctx.beginPath()
      ctx.strokeStyle = color
      ctx.lineWidth = lineWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'

      // Calculate average amplitude from volumes (0-1 range)
      const avgVolume = smoothedVolumes.reduce((a, b) => a + b, 0) / smoothedVolumes.length
      const baseAmplitude = Math.min(avgVolume * 35 + 1, rect.height / 2 - 4)

      // Update phase for continuous animation
      phaseRef.current += 0.03

      // Draw sine wave with smooth curves
      const segments = 80
      const frequency = 2.5 // number of wave cycles

      const points: { x: number; y: number }[] = []

      for (let i = 0; i <= segments; i++) {
        const x = (i / segments) * width
        const progress = i / segments

        // Use volume data to modulate the wave amplitude along its length
        const volumeIndex = Math.min(
          Math.floor(progress * smoothedVolumes.length),
          smoothedVolumes.length - 1
        )
        const localVolume = smoothedVolumes[volumeIndex] || 0
        const localAmplitude = baseAmplitude * (0.4 + localVolume * 0.8)

        // Create organic wave shape with phase animation
        const waveValue =
          Math.sin(progress * Math.PI * 2 * frequency + phaseRef.current) * 0.6 +
          Math.sin(progress * Math.PI * 2 * frequency * 1.5 + phaseRef.current * 0.7) * 0.3 +
          Math.sin(progress * Math.PI * 2 * frequency * 0.5 + phaseRef.current * 1.3) * 0.1

        const y = centerY + waveValue * localAmplitude

        points.push({ x, y })
      }

      // Draw using bezier curves for extra smoothness
      if (points.length > 0) {
        ctx.moveTo(points[0].x, points[0].y)

        for (let i = 1; i < points.length - 2; i++) {
          const xc = (points[i].x + points[i + 1].x) / 2
          const yc = (points[i].y + points[i + 1].y) / 2
          ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc)
        }

        // Connect last two points
        if (points.length > 2) {
          const lastIdx = points.length - 1
          ctx.quadraticCurveTo(
            points[lastIdx - 1].x,
            points[lastIdx - 1].y,
            points[lastIdx].x,
            points[lastIdx].y
          )
        }
      }

      ctx.stroke()

      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [volumes, color, lineWidth])

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{ width: '100%', height: '100%' }}
    />
  )
}
